import { useState } from "react";
import { Trash2, CheckCircle2 } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

/**
 * Clears every localStorage key written by the daily-mode features
 * (Element of the Day + Chem-doku Puzzle of the Day): per-day results,
 * in-progress boards, and streak counters. Lives on /privacy so users
 * can reset their daily footprint without nuking other preferences.
 */
const DAILY_KEY_PREFIXES = [
  "elementpad:daily:",
  "elementpad:chemdoku-daily:",
];

export default function DailyDataResetButton() {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);

  function handleClear() {
    if (typeof window === "undefined") return;
    let removed = 0;
    try {
      const ls = window.localStorage;
      const toRemove: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i);
        if (!key) continue;
        if (DAILY_KEY_PREFIXES.some((p) => key.startsWith(p))) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => ls.removeItem(k));
      removed = toRemove.length;
    } catch {
      toast({
        title: "Couldn't clear data",
        description: "Your browser blocked access to local storage.",
      });
      return;
    }
    setConfirming(false);
    toast({
      title: "Daily data cleared",
      description:
        removed === 0
          ? "Nothing to clear - no daily progress was stored."
          : `Removed ${removed} item${removed === 1 ? "" : "s"}. Streaks reset to 0.`,
    });
  }

  return (
    <div className="mt-4 p-3 rounded-lg border border-border bg-card/50">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground">
            Reset daily-mode data
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            Clears Element of the Day and Chem-doku Puzzle of the Day results,
            in-progress boards, and streak counters from this browser. Other
            preferences (theme, accessibility, flashcards) are untouched.
          </p>
        </div>
        {confirming ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button size="sm" variant="destructive" onClick={handleClear}>
              <CheckCircle2 size={12} className="mr-1" aria-hidden="true" />
              Confirm clear
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirming(true)}
            className="flex-shrink-0"
          >
            <Trash2 size={12} className="mr-1" aria-hidden="true" />
            Clear daily data
          </Button>
        )}
      </div>
    </div>
  );
}